import { useState, useEffect, useCallback } from "react";
import { Loader2, RefreshCw, Trash2, Plus, Download, Pencil, X, Save, DollarSign } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "advocacy_contact", label: "Advocacy Contact" },
  { value: "safety_planning", label: "Safety Planning" },
  { value: "referral", label: "Referral" },
  { value: "transportation", label: "Transportation" },
  { value: "emergency_assistance", label: "Emergency Assistance" },
  { value: "workshop_enrollment", label: "Workshop Enrollment" },
  { value: "workshop_completion", label: "Workshop Completion" },
  { value: "training_delivered", label: "Training Delivered" },
  { value: "employment_outcome", label: "Employment Outcome" },
  { value: "follow_up", label: "Follow-Up" },
  { value: "other", label: "Other" },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const REFERRAL_STATUSES = ["", "new", "accepted", "declined", "completed", "no_response"];
const EMPLOYMENT_STATUSES = ["", "actively_searching", "in_training", "placed", "retained_30d", "retained_90d", "lost"];
const FOLLOW_UP_STATUSES = ["", "scheduled", "attempted", "reached", "closed"];

const empty = () => ({
  date: new Date().toISOString().slice(0, 10),
  category: "advocacy_contact",
  participant_ref: "",
  county: "",
  referral_destination: "",
  referral_status: "",
  expense_usd: "",
  training_audience: "",
  attendees: "",
  workshop_name: "",
  employment_status: "",
  follow_up_status: "",
  notes: "",
});

export const AdminServiceDeliveryPanel = ({ getAuthHeader }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/admin/service-delivery`, getAuthHeader()),
        axios.get(`${API}/admin/service-delivery/summary`, getAuthHeader()),
      ]);
      setRows(listRes.data);
      setSummary(sumRes.data);
    } catch {
      toast.error("Failed to load service delivery records");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty()); setEditing(null); setShowForm(true); };
  const openEdit = (r) => {
    setForm({
      ...empty(),
      ...r,
      expense_usd: r.expense_usd ?? "",
      attendees: r.attendees ?? "",
    });
    setEditing(r.id);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const save = async () => {
    const payload = {
      ...form,
      expense_usd: form.expense_usd === "" ? null : parseFloat(form.expense_usd),
      attendees: form.attendees === "" ? null : parseInt(form.attendees, 10),
    };
    try {
      if (editing) {
        await axios.patch(`${API}/admin/service-delivery/${editing}`, payload, getAuthHeader());
        toast.success("Record updated");
      } else {
        await axios.post(`${API}/admin/service-delivery`, payload, getAuthHeader());
        toast.success("Record created");
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await axios.delete(`${API}/admin/service-delivery/${id}`, getAuthHeader());
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/service-delivery/export`, { ...getAuthHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pillar-service-delivery-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Export failed");
    }
  };

  const filtered = filter ? rows.filter((r) => r.category === filter) : rows;

  return (
    <div className="space-y-5" data-testid="service-delivery-panel">
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SumCard label="Total Records" value={summary.total_records} testId="sd-total" />
          <SumCard label="Emergency & Transport Expense" value={`$${summary.total_expense_usd.toLocaleString()}`} testId="sd-expense" />
          <SumCard label="Training Attendees (aggregate)" value={summary.total_attendees} testId="sd-attendees" />
          <SumCard label="Categories Tracked" value={summary.by_category.length} testId="sd-cats" />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white"
            data-testid="sd-filter"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={load} data-testid="sd-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="sd-export">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
        <Button size="sm" onClick={openNew} className="bg-warm-700 hover:bg-warm-800 text-white" data-testid="sd-new">
          <Plus className="w-4 h-4 mr-1.5" /> New Record
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-500">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-warm-700" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white border border-gray-100 rounded-xl">
          <p className="text-sm">No service delivery records yet. Click "New Record" to add one.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
                <tr>
                  <Th>Date</Th><Th>Category</Th><Th>Participant</Th><Th>County</Th>
                  <Th>Referral</Th><Th>Expense</Th><Th>Details</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`sd-row-${r.id}`}>
                    <Td>{r.date}</Td>
                    <Td><span className="text-xs bg-warm-100 text-warm-800 px-2 py-0.5 rounded-full">{CATEGORY_LABEL[r.category] || r.category}</span></Td>
                    <Td className="text-xs">{r.participant_ref || "—"}</Td>
                    <Td className="text-xs">{r.county || "—"}</Td>
                    <Td className="text-xs">
                      {r.referral_destination ? (
                        <div>
                          <div>{r.referral_destination}</div>
                          {r.referral_status && <div className="text-gray-500">{r.referral_status}</div>}
                        </div>
                      ) : "—"}
                    </Td>
                    <Td className="text-xs">{r.expense_usd ? `$${r.expense_usd.toFixed(2)}` : "—"}</Td>
                    <Td className="text-xs max-w-xs">
                      {r.workshop_name && <div>Workshop: {r.workshop_name}</div>}
                      {r.training_audience && <div>Audience: {r.training_audience} ({r.attendees || 0})</div>}
                      {r.employment_status && <div>Employment: {r.employment_status}</div>}
                      {r.follow_up_status && <div>Follow-up: {r.follow_up_status}</div>}
                      {r.notes && <div className="text-gray-500 truncate" title={r.notes}>Notes: {r.notes}</div>}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(r)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="Edit" data-testid={`sd-edit-${r.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(r.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete" data-testid={`sd-delete-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeForm}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sd-form-modal">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
                {editing ? "Edit Record" : "New Service Delivery Record"}
              </h3>
              <button onClick={closeForm} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" data-testid="sd-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500 bg-gray-50 rounded p-3">
                Do not enter personally identifying info in the Participant field. Use initials or an internal reference (e.g. "TP-2026-042").
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <FormInput label="Date" type="date" required value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="sd-f-date" />
                <FormSelect label="Category" required value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} testId="sd-f-category" />
                <FormInput label="Participant Reference" placeholder="Initials or internal ref" value={form.participant_ref} onChange={(v) => setForm({ ...form, participant_ref: v })} testId="sd-f-participant" />
                <FormInput label="County / City" value={form.county} onChange={(v) => setForm({ ...form, county: v })} testId="sd-f-county" />
                <FormInput label="Referral Destination" value={form.referral_destination} onChange={(v) => setForm({ ...form, referral_destination: v })} testId="sd-f-ref-dest" />
                <FormSelect label="Referral Status" value={form.referral_status} onChange={(v) => setForm({ ...form, referral_status: v })} options={REFERRAL_STATUSES.map((s) => ({ value: s, label: s || "—" }))} testId="sd-f-ref-status" />
                <FormInput label="Expense (USD)" type="number" step="0.01" min="0" placeholder="0.00" value={form.expense_usd} onChange={(v) => setForm({ ...form, expense_usd: v })} testId="sd-f-expense" />
                <FormInput label="Attendees" type="number" min="0" value={form.attendees} onChange={(v) => setForm({ ...form, attendees: v })} testId="sd-f-attendees" />
                <FormInput label="Training Audience" placeholder="e.g. Baltimore County PD - Dundalk" value={form.training_audience} onChange={(v) => setForm({ ...form, training_audience: v })} testId="sd-f-audience" />
                <FormInput label="Workshop Name" placeholder="e.g. Resume Workshop, ServSafe" value={form.workshop_name} onChange={(v) => setForm({ ...form, workshop_name: v })} testId="sd-f-workshop" />
                <FormSelect label="Employment Status" value={form.employment_status} onChange={(v) => setForm({ ...form, employment_status: v })} options={EMPLOYMENT_STATUSES.map((s) => ({ value: s, label: s || "—" }))} testId="sd-f-emp-status" />
                <FormSelect label="Follow-Up Status" value={form.follow_up_status} onChange={(v) => setForm({ ...form, follow_up_status: v })} options={FOLLOW_UP_STATUSES.map((s) => ({ value: s, label: s || "—" }))} testId="sd-f-fu-status" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">Notes</label>
                <textarea
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm min-h-[80px]"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="sd-f-notes"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={closeForm} data-testid="sd-cancel">Cancel</Button>
                <Button onClick={save} className="bg-warm-700 hover:bg-warm-800 text-white" data-testid="sd-save">
                  <Save className="w-4 h-4 mr-1.5" /> Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function SumCard({ label, value, testId }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4" data-testid={testId}>
      <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-semibold text-gray-900 mt-1" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{value}</p>
    </div>
  );
}

function FormInput({ label, value, onChange, required, type = "text", placeholder, step, min, testId }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        required={required}
        step={step}
        min={min}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function FormSelect({ label, value, onChange, options, required, testId }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
        data-testid={testId}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-2 font-medium">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
